/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, Card, CardHeader, Container, createStyles, List, ListItemText, TablePagination, Theme, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router";
import { createSelector } from "reselect";
import { AppState, createEval, loadEvaluations, syncSystem } from "../../redux";
import { selectEvals, selectUserId } from "../hooks";
import { ErrorBlock } from './form/components/ErrorBlock';
import LoadingIndicator from './form/components/LoadingIndicator';
import WmpAuditListItem from "./WmpAuditListItem";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      zIndex: 0,
      WebkitOverflowScrolling: "touch",
    },
    buttonGroupContainer: {
      display: "flex",
      flex: 1,
      alignItems: "center",
    },
    modeButton: {
      textTransform: "none",
    },
    modeButtonRoot: {
      padding: theme.spacing(0,3),
      height: "32px",
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    content: {
      flex: 1,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    noEvalsMessage: {
      margin: theme.spacing(1, 3),
    },
    list: {
      marginBottom: theme.spacing(10),
      display: "flex",
      flexDirection: "column",
    },
    loading: {
      backgroundColor: "#fff",
    },
    pagination: {},
  })
);

const selectError = createSelector(
  (state: AppState) => state.evals.error,
  error => error
);
const selectLoadingEvals = createSelector(
  (state: AppState) => state.evals.loading,
  loading => loading
);
const selectEvalLimit = createSelector(
  (state: AppState) => state.evals.limit,
  limit => limit
);
const selectEvalOffset = createSelector(
  (state: AppState) => state.evals.offset,
  offset => offset
);
const selectEvalCount = createSelector(
  (state: AppState) => state.evals.total,
  count => count
);
export const selectWmp = (wmpId: string) => createSelector(
  (state: AppState) => state.wmps.items,
  items => items[wmpId]
);

interface WmpAuditListProperties extends RouteComponentProps<{
  wmpId: string;
}>{};

const WmpAuditList: FC<WmpAuditListProperties> = props => {
  const WMP_AUDIT = 'wmp_audit';
  const classes = useStyles(props);
  const evals = useSelector(selectEvals);
  const error = useSelector(selectError);
  const accountId = useSelector(selectUserId);
  const loadingEvals = useSelector(selectLoadingEvals);

  const totalEvals = useSelector(selectEvalCount);
  const limit = useSelector(selectEvalLimit);
  const offset = useSelector(selectEvalOffset);

  const dispatch = useDispatch();
  const history = useHistory();

  const fetchingEvals = useSelector((state: AppState) => state.evals.isFetching);
  const wmpId = props.match.params.wmpId;
  const wmp = useSelector(selectWmp(wmpId));

  useEffect(() => {
    if (!fetchingEvals) {
      dispatch(loadEvaluations(null, 25, 0, WMP_AUDIT, wmp?.facilityId));
    }
  }, [fetchingEvals, dispatch, wmp])

  useEffect(
    function refresh() {
      dispatch(syncSystem());
    },
    [accountId, dispatch]
  );

  function handleCreate(type: string) {
    dispatch(createEval(type, history));
  }

  if (error) {
    return (
      <div className={classes.root}>
        <ErrorBlock name={error.name} error={error} message={error.message} />
      </div>
    );
  }

  const content =
    evals.length > 0 ? (
      <>
        {evals.map(function(evlId) {
          return <WmpAuditListItem evalID={evlId} key={evlId} />;
        })}
      </>
    ) : (
      <Typography className={classes.noEvalsMessage}>
        No ACC Audits yet
      </Typography>
    );

  return (
    <div className={classes.root}>
      <div style={{
        flex: 1,
        display: 'flex',
        overflowY: 'scroll',
      }}>
        <Container maxWidth="md" className={classes.content}>
          <div style={{ 'paddingBottom': '10px', 'textAlign': 'end' }}>
            <Button
              aria-controls="new-menu"
              aria-haspopup="true"
              variant="contained"
              color="secondary"
              onClick={() => {
                handleCreate(WMP_AUDIT);
              }}
            >
              New Audit
            </Button>
          </div>
          <Card>
            <CardHeader title={'ACC Audits'} style={{ 'backgroundColor': '#234495', 'color': 'white', 'padding': '8px 16px' }} titleTypographyProps={{variant:'h6'}} />
              <List>{(fetchingEvals || loadingEvals) ? <LoadingIndicator>Loading ACC Audits</LoadingIndicator> : content}</List>
              <TablePagination
                className={classes.pagination}
                count={totalEvals}
                component="div"
                rowsPerPage={limit}
                page={offset === 0 ? 0 : offset / limit}
                onChangePage={(event, newPage) => dispatch(loadEvaluations(null, limit, newPage * limit, WMP_AUDIT, wmp?.facilityId))}
                onChangeRowsPerPage={event => dispatch(loadEvaluations(null, parseInt(event!.target.value), 0, WMP_AUDIT, wmp?.facilityId))}
              />
            </Card>
        </Container>
      </div>
    </div>
  );
};

export default WmpAuditList;
